import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import organizationServiceApi from 'shared/api/organization.service.api';
import { notification, Tag } from 'antd';
import {
  Table,
  cellActionKeyboardNavigate,
  DateCell,
  TagListCell,
} from 'src/components/design-system/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faPencil } from '@fortawesome/pro-solid-svg-icons';
import { Loader, Tooltip } from 'src/components/core';
import styles from './magic-link-management.module.scss';
import { getLang } from 'shared/store/selectors/lang.selector';
import { Button, ScreenTitle, Select } from 'src/components/design-system';
import { CopyToClipboard } from 'src/components/common/copy-to-clipboard';
import { ActionButton } from 'src/components/common';
import { Modal } from 'src/components/design-system/modal';
import { useFormik } from 'formik';
import ipRegex from 'ip-regex';
import classNames from 'classnames';
import { AllSettingsButton } from '../../settings.page.component';
import { getWorkspaces } from 'shared/store/selectors/workspace.selector';

function hideLink(link) {
  return link.replace(/.(?=.{4})/g, '');
}

export function MagicLinkManagementPage() {
  const organizationId = useSelector(state => state.organization.id);
  const lang = useSelector(getLang('SETTINGS'));
  const [magicLinks, setMagicLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [api, contextHolder] = notification.useNotification();
  const workspaces = useSelector(getWorkspaces);

  function validatePayload(values) {
    const errors = {};

    // Check if domains or IP ranges have at least one value

    if (!values.allowDomains.length && !values.allowIpRanges.length) {
      errors.allowDomains = 'At least one domain or IP address is required.';
      errors.allowIpRanges = 'At least one domain or IP address is required.';
    }

    // Validate domains
    const domainPattern = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    values.allowDomains.forEach(domain => {
      if (!domainPattern.test(domain)) {
        errors.allowDomains = errors.allowDomains || [];
        errors.allowDomains.push(`${domain} is not a valid domain.`);
      }
    });

    // Validate IP ranges and add CIDR if missing
    values.allowIpRanges.forEach(ip => {
      const [ipRange, cidr] = ip.split('/');
      if (!ipRegex({ exact: true }).test(ipRange)) {
        errors.allowIpRanges = errors.allowIpRanges || [];
        errors.allowIpRanges.push(`${ipRange} is not a valid IP address.`);
      }
      if (!cidr) {
        errors.allowIpRanges = errors.allowIpRanges || [];
        errors.allowIpRanges.push(`CIDR is missing for ${ipRange}.`);
      } else {
        if (cidr < 0 || cidr > 32) {
          errors.allowIpRanges = errors.allowIpRanges || [];
          errors.allowIpRanges.push(`${cidr} is not a valid CIDR range.`);
        }
      }
    });

    return errors;
  }

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await organizationServiceApi.getMagicLinks({ organizationId });

      if (response.error) {
        console.log('error', response.error);
        setMagicLinks([]);
      } else {
        setMagicLinks(response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  const onSubmit = async () => {
    setEditLoading(true);
    try {
      const serviceMethod = selectedId
        ? organizationServiceApi.updateMagicLink
        : organizationServiceApi.generateMagicLink;

      const toastMessage = selectedId ? lang.LINK_UPDATED : lang.LINK_CREATED;

      const response = await serviceMethod({
        organizationId,
        magicLinkId: selectedId,
        allowDomains: values.allowDomains,
        allowIpRanges: values.allowIpRanges,
        defaultWorkspaceId: values.defaultWorkspaceId,
      });

      if (response.error) {
        api.error({
          title: 'Something went wrong, please try again later',
          placement: 'bottomRight',
        });
        return;
      }
      api.success({
        title: toastMessage,
        placement: 'bottomRight',
      });
    } catch (error) {
      api.error({
        title: 'Something went wrong, please try again later',
        placement: 'bottomRight',
      });
    } finally {
      closeModal();
      fetchData();
    }
  };

  const closeModal = () => {
    setSelectedId(null);
    setEditModalOpen(false);
    resetForm();
    setEditLoading(false);
  };

  const handleEdit = useCallback(({ id }) => {
    setSelectedId(id);
  }, []);

  const { handleSubmit, setFieldValue, resetForm, values, errors, touched, isValid } = useFormik({
    enableReinitialize: true,
    validate: validatePayload,
    initialValues: {
      allowDomains: selectedId ? magicLinks.find(({ id }) => selectedId === id)?.allowDomains : [],
      allowIpRanges: selectedId
        ? magicLinks.find(({ id }) => selectedId === id)?.allowIpRanges
        : [],
      defaultWorkspaceId: selectedId
        ? magicLinks.find(({ id }) => selectedId === id)?.defaultWorkspaceId
        : null,
    },
    onSubmit,
  });

  useEffect(() => {
    if (selectedId) {
      setEditModalOpen(true);
    }
  }, [selectedId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const workspaceOptions = useMemo(
    () =>
      workspaces
        .filter(({ restricted }) => !restricted)
        .map(({ id, name }) => ({
          value: id,
          label: name,
        })),
    [workspaces],
  );

  const columns = useMemo(
    /**
     * @type {() => import('ag-grid-community').ColDef[]}
     */
    () => [
      {
        headerName: lang.LINK,
        field: 'link',
        flex: 1,
        cellRenderer: params => (
          <div className={styles.linkWrapper}>
            <p>{hideLink(params.value)}</p>
            <Tooltip title={lang.COPY}>
              <CopyToClipboard text={params.value}>
                <FontAwesomeIcon icon={faCopy} className={styles.copyIcon} />
              </CopyToClipboard>
            </Tooltip>
          </div>
        ),
        suppressKeyboardEvent: cellActionKeyboardNavigate,
      },
      {
        headerName: lang.ALLOWED_DOMAINS,
        field: 'allowDomains',
        flex: 1,
        filter: true,
        cellRenderer: TagListCell,
      },
      {
        headerName: lang.ALLOWED_IP_RANGES,
        field: 'allowIpRanges',
        flex: 1,
        filter: true,
        cellRenderer: TagListCell,
      },
      {
        headerName: lang.DEFAULT_WORKSPACE,
        field: 'defaultWorkspaceId',
        flex: 1,
        filter: true,
        cellRenderer: params => {
          const workspace = workspaces.find(({ id }) => id === params.value);
          if (!workspace) return '-';

          const restrictedMsg = workspace.restricted
            ? lang.RESTRICT_ERROR_MASSAGE_MAGIC_LINK.replace('{WORKSPACE}', `"${workspace.name}"`)
            : null;

          if (restrictedMsg) {
            return (
              <Tooltip title={restrictedMsg}>
                <Tag color="red">{workspace.name}</Tag>
              </Tooltip>
            );
          }
          return <Tag>{workspace.name}</Tag>;
        },
      },
      {
        headerName: lang.CREATED_AT,
        field: 'createdAt',
        flex: 1,
        cellRenderer: DateCell,
        cellRendererParams: { format: 'longDateTime' },
      },
      {
        headerName: lang.UPDATED_AT,
        field: 'updatedAt',
        flex: 1,
        cellRenderer: DateCell,
        cellRendererParams: { format: 'longDateTime' },
      },
      {
        headerName: lang.ACTIONS,
        field: 'actions',
        sortable: false,
        cellRenderer: params => (
          <Tooltip title={lang.EDIT}>
            <Button type="link" onClick={() => handleEdit(params.data)}>
              <FontAwesomeIcon icon={faPencil} />
            </Button>
          </Tooltip>
        ),
        suppressKeyboardEvent: cellActionKeyboardNavigate,
      },
    ],
    [lang, workspaces, handleEdit],
  );

  return (
    <>
      {contextHolder}
      {loading ? (
        <Loader />
      ) : magicLinks ? (
        <div className={classNames(styles.root, 'new-design-system')}>
          <AllSettingsButton />
          <Modal
            title={selectedId ? lang.UPDATE_LINK_TITLE : lang.NEW_LINK_TITLE}
            open={editModalOpen}
            onCancel={closeModal}
            onSubmit={handleSubmit}
            cancelText={lang.CANCEL}
            submitText={selectedId ? lang.UPDATE : lang.CREATE}
            submitDisabled={!isValid}
            loading={editLoading}
            className={styles.modal}>
            <Select
              label={lang.ALLOWED_IP_RANGES}
              mode="tags"
              value={values.allowIpRanges}
              error={errors.allowIpRanges}
              touched={touched.allowIpRanges}
              onChange={setFieldValue}
              name="allowIpRanges"
              size="large"
            />

            <label className={classNames(styles.inputLabel, styles.bold)}>
              {lang.WARNING_DOMAIN}
            </label>
            <Select
              mode="tags"
              label={lang.ALLOWED_DOMAINS}
              value={values.allowDomains}
              error={errors.allowDomains}
              touched={touched.allowDomains}
              onChange={setFieldValue}
              name="allowDomains"
              size="large"
            />

            <Select
              options={workspaceOptions}
              value={values.defaultWorkspaceId}
              name="defaultWorkspaceId"
              onChange={setFieldValue}
              label={lang.DEFAULT_WORKSPACE}
              size="large"
            />
          </Modal>
          <div className={styles.header}>
            <ScreenTitle title={lang.MAGIC_LINK} containerStyle={styles.title} />
            <ActionButton
              onClick={() => setEditModalOpen(true)}
              labelText={lang.NEW_LINK}
              positionAbsolute={false}
              className={styles.newLinkButton}
            />
          </div>
          <Table
            containerProps={{ className: styles.table }}
            columnDefs={columns}
            rowData={magicLinks}
            getRowId={params => params.data.id}
            domLayout="autoHeight"
            enableColumnChooser
            paginationPageSize={20}
          />
        </div>
      ) : null}
    </>
  );
}
